import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { searchListings } from '../services/api';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    serviceType: 'All Services',
    availability: 'Any Time',
    rating: 'Any Rating',
  });

  const query = searchParams.get('q');

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await searchListings(query, filters);
        setResults(data || []);
      } catch (err) {
        setError('Failed to fetch results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, filters]);

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({ ...prev, [type]: value }));
  };

  if (loading) {
    return (
      <div className="search-results-container">
        <h1>Searching for "{query}"</h1>
        <div className="loading-skeleton">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-image" />
              <div className="skeleton-content">
                <div className="skeleton-line" style={{ width: '60%' }} />
                <div className="skeleton-line" style={{ width: '80%' }} />
                <div className="skeleton-line" style={{ width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="search-results-container">
        <div className="error-state">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results-container">
      <div className="search-header">
        <h1>Search Results for "{query}"</h1>
        <div className="breadcrumb">
          <Link to="/">Home</Link> / Search Results
        </div>
      </div>

      <div className="search-content">
        <aside className="filters-sidebar">
          <h2>Filters</h2>
          
          <div className="filter-group">
            <h3>Service Type</h3>
            <select
              value={filters.serviceType}
              onChange={(e) => handleFilterChange('serviceType', e.target.value)}
            >
              <option>All Services</option>
              <option>Emergency Repair</option>
              <option>Maintenance</option>
              <option>Installation</option>
            </select>
          </div>

          <div className="filter-group">
            <h3>Availability</h3>
            <select
              value={filters.availability}
              onChange={(e) => handleFilterChange('availability', e.target.value)}
            >
              <option>Any Time</option>
              <option>24/7</option>
              <option>Business Hours</option>
              <option>Weekends</option>
            </select>
          </div>

          <div className="filter-group">
            <h3>Rating</h3>
            <select
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
            >
              <option>Any Rating</option>
              <option>4+ Stars</option>
              <option>3+ Stars</option>
              <option>2+ Stars</option>
            </select>
          </div>
        </aside>

        <main className="results-grid">
          {results.length === 0 ? (
            <div className="empty-state">
              <h2>No results found</h2>
              <p>Try adjusting your search criteria or filters</p>
              <div className="suggestions">
                <h3>Popular searches:</h3>
                <ul>
                  <li><Link to="/search?q=oven repair">Oven Repair</Link></li>
                  <li><Link to="/search?q=refrigerator">Refrigerator Service</Link></li>
                  <li><Link to="/search?q=emergency">Emergency Repairs</Link></li>
                </ul>
              </div>
            </div>
          ) : (
            results.map((result) => (
              <Link
                key={result.id}
                to={`/listing/${result.id}`}
                className="result-card"
              >
                <div className="result-image">
                  <img src={result.image} alt={result.title} />
                  <div className="result-rating">
                    <span className="rating-star">★</span>
                    <span className="rating-number">{result.rating}</span>
                    <span className="rating-count">({result.reviews})</span>
                  </div>
                </div>
                <div className="result-content">
                  <h3>{result.title}</h3>
                  <p className="result-description">{result.description}</p>
                  <div className="result-location">
                    <span className="location-icon">📍</span>
                    {result.location}
                  </div>
                  <div className="result-services">
                    {result.services.map((service, index) => (
                      <span key={index} className="service-tag">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))
          )}
        </main>
      </div>
    </div>
  );
};

export default SearchResults;
